import React, { useMemo } from 'react';
import { getCarouselPreset, publicAssetUrl } from './presets';
import {
  buildCarouselPresetFromDraft,
  buildHighlightBackground,
  GenericCarouselBrandPresetValues,
} from './brandPresets';
import {
  createCanvasMeasurer,
  fitBodyText,
  fitTextBlock,
  isHighlightedWord,
  TextMeasurer,
  FitLayout,
} from './textFit';

const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1350;

export interface GenericCarouselImage {
  id?: string;
  url: string;
  title: string;
  explanation: string;
}

export interface GenericCarouselFrameProps {
  variant: 'headline' | 'slide';
  slideIndex?: number;
  headline: string;
  brandingTag: string;
  images: GenericCarouselImage[];
  assets?: {
    frontLogoUrl?: string;
    gradientUrl?: string;
  };
  stylePresetDraft?: GenericCarouselBrandPresetValues;
}

export const GenericCarouselFrame: React.FC<GenericCarouselFrameProps> = (props) => {
  const preset = buildCarouselPresetFromDraft(props.stylePresetDraft, props.brandingTag);
  const brandingTag = props.stylePresetDraft?.brandTag || props.brandingTag;
  const measure = useMemo(() => createCanvasMeasurer(), []);
  const currentImage =
    props.variant === 'headline' ? props.images[0] : props.images[(props.slideIndex ?? 0) + 1];

  if (!currentImage && preset.layoutVariant !== 'no-image-typography') {
    return <div style={frameStyle} />;
  }

  const currentSlide = currentImage || {
    url: '',
    title: props.headline,
    explanation: '',
  };
  const isCtaSlide = props.variant === 'slide' && currentSlide.explanation.trim().length === 0;
  const title = props.variant === 'headline' ? props.headline : currentSlide.title;
  const titleLayout = fitTextBlock(title, {
    minLines: props.variant === 'headline' || isCtaSlide ? 2 : 1,
    maxLines: props.variant === 'headline' || isCtaSlide ? 3 : 2,
    maxWidth: CANVAS_WIDTH - preset.layout.sidePadding * 2,
    maxHeight: props.variant === 'headline' || isCtaSlide ? 520 : 250,
    minFontSize: preset.headline.minFontSize,
    maxFontSize: preset.headline.maxFontSize,
    lineHeightRatio: preset.headline.lineHeightRatio,
    wordGap: preset.headline.wordGap,
    fontFamily: preset.fonts.headline,
    fontWeight: preset.headline.fontWeight,
    measure,
  });

  if (preset.layoutVariant !== 'image-led') {
    return (
      <GeneralizedCarouselLayout
        brandingTag={brandingTag}
        currentImage={currentImage}
        images={props.images}
        preset={preset}
        slideIndex={props.slideIndex ?? 0}
        title={title}
        titleLayout={titleLayout}
        explanation={currentSlide.explanation}
        measure={measure}
      />
    );
  }

  return (
    <div style={frameStyle}>
      <img src={currentSlide.url} alt="" style={backgroundImageStyle} draggable={false} />
      {props.variant === 'headline' ? (
        <HeadlineContent
          layout={titleLayout}
          gradientUrl={resolveAsset(props.assets?.gradientUrl ?? preset.assets.gradient)}
          logoUrl={resolveAsset(props.assets?.frontLogoUrl ?? preset.assets.frontLogo)}
          preset={preset}
          titleBottom={centerYToTop(preset.layout.swipeY - preset.headline.paddingBottom)}
        />
      ) : (
        <>
          <TopBrand preset={preset} brandingTag={brandingTag} />
          {isCtaSlide ? (
            <>
              <BottomGradient fadeTop={560} preset={preset} />
              <HeadlineText
                layout={titleLayout}
                gradientUrl={resolveAsset(props.assets?.gradientUrl ?? preset.assets.gradient)}
                preset={preset}
                titleBottom={centerYToTop(preset.layout.swipeY - preset.headline.paddingBottom)}
              />
            </>
          ) : (
            <SlideContent
              titleLayout={titleLayout}
              explanation={currentImage.explanation}
              gradientUrl={resolveAsset(props.assets?.gradientUrl ?? preset.assets.gradient)}
              measure={measure}
              preset={preset}
            />
          )}
          <BottomRule logoUrl={resolveAsset(props.assets?.frontLogoUrl ?? preset.assets.frontLogo)} preset={preset} />
        </>
      )}
    </div>
  );
};

const HeadlineContent: React.FC<{
  layout: FitLayout;
  gradientUrl: string;
  logoUrl: string;
  preset: ReturnType<typeof getCarouselPreset>;
  titleBottom: number;
}> = ({ layout, gradientUrl, logoUrl, preset, titleBottom }) => {
  const titleTop = titleBottom - layout.totalHeight;
  const lineY = titleTop - 35;

  return (
    <>
      <BottomGradient fadeTop={Math.max(0, lineY - 140)} preset={preset} />
      <DecorativeRule preset={preset} y={lineY} xStart={160} xEnd={480} />
      <img
        src={logoUrl}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          left: CANVAS_WIDTH / 2 - preset.layout.logoWidth / 2,
          top: lineY - preset.layout.logoWidth / 2,
          width: preset.layout.logoWidth,
        }}
      />
      <HeadlineText layout={layout} gradientUrl={gradientUrl} preset={preset} titleBottom={titleBottom} />
      <div
        style={{
          ...headlineFont(preset, 30),
          position: 'absolute',
          top: centerYToTop(preset.layout.swipeY) - 20,
          left: 0,
          width: '100%',
          textAlign: 'center',
          color: preset.colors.primary,
          textShadow: '0 4px 10px rgba(0,0,0,0.65)',
        }}
      >
        SWIPE FOR MORE -&gt;
      </div>
    </>
  );
};

const SlideContent: React.FC<{
  titleLayout: FitLayout;
  explanation: string;
  gradientUrl: string;
  measure: TextMeasurer;
  preset: ReturnType<typeof getCarouselPreset>;
}> = ({ titleLayout, explanation, gradientUrl, measure, preset }) => {
  const contentBottom = CANVAS_HEIGHT - 90;
  const titleBodyGap = 50;
  const maxBodyHeight = Math.max(130, contentBottom - titleBodyGap - titleLayout.totalHeight - 760);
  const fittedBody = fitBodyText(
    explanation,
    CANVAS_WIDTH - preset.layout.sidePadding * 2,
    maxBodyHeight,
    preset.explanation.minFontSize,
    preset.explanation.maxFontSize,
    preset.explanation.lineHeightRatio,
    preset.fonts.body,
    preset.explanation.fontWeight,
    measure
  );
  const explanationHeight = fittedBody.lines.length * fittedBody.lineHeight;
  const explanationTop = contentBottom - explanationHeight;
  const titleBottom = explanationTop - titleBodyGap;
  const titleTop = titleBottom - titleLayout.totalHeight;

  return (
    <>
      <BottomGradient fadeTop={Math.max(0, titleTop - 150)} preset={preset} />
      <HeadlineText layout={titleLayout} gradientUrl={gradientUrl} preset={preset} titleBottom={titleBottom} />
      <div
        style={{
          position: 'absolute',
          top: explanationTop,
          left: preset.layout.sidePadding,
          width: CANVAS_WIDTH - preset.layout.sidePadding * 2,
          textAlign: 'center',
          color: preset.colors.secondary,
          fontFamily: preset.fonts.body,
          fontSize: fittedBody.fontSize,
          fontWeight: preset.explanation.fontWeight,
          lineHeight: `${fittedBody.lineHeight}px`,
          textShadow: '0 2px 10px rgba(0,0,0,0.65)',
          whiteSpace: 'pre',
        }}
      >
        {fittedBody.lines.join('\n')}
      </div>
    </>
  );
};

const GeneralizedCarouselLayout: React.FC<{
  brandingTag: string;
  currentImage?: GenericCarouselImage;
  images: GenericCarouselImage[];
  preset: ReturnType<typeof getCarouselPreset>;
  slideIndex: number;
  title: string;
  titleLayout: FitLayout;
  explanation: string;
  measure: TextMeasurer;
}> = ({ brandingTag, currentImage, images, preset, slideIndex, title, titleLayout, explanation, measure }) => {
  const imageUrl = currentImage?.url;
  const nextImage = images[slideIndex + 2] || images[0] || currentImage;

  if (preset.layoutVariant === 'no-image-typography') {
    return (
      <div style={{ ...frameStyle, backgroundColor: '#F2F2EE' }}>
        <div style={lightBrandStyle}>{brandingTag.toUpperCase()}</div>
        <EditorialTitle title={title} preset={preset} top={260} color="#101010" accent={preset.colors.accent} />
        {explanation && <EditorialBody explanation={explanation} preset={preset} measure={measure} top={850} color="#101010" />}
      </div>
    );
  }

  if (preset.layoutVariant === 'chat-overlay') {
    return (
      <div style={frameStyle}>
        {imageUrl && <img src={imageUrl} alt="" style={backgroundImageStyle} draggable={false} />}
        <BottomGradient fadeTop={0} preset={{ ...preset, effects: { ...preset.effects, bottomOverlayStrength: 0.78 } }} />
        <TopBrand preset={preset} brandingTag={brandingTag} />
        <ChatBubble text={title} top={395} align="left" preset={preset} />
        {explanation && <ChatBubble text={explanation} top={585} align="right" preset={preset} secondary />}
        <HeadlineText layout={titleLayout} gradientUrl={resolveAsset(preset.assets.gradient)} preset={preset} titleBottom={1180} />
      </div>
    );
  }

  if (preset.layoutVariant === 'split-comparison') {
    return (
      <div style={frameStyle}>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: '#050505' }}>
          {imageUrl && <img src={imageUrl} alt="" style={{ ...backgroundImageStyle, position: 'relative' }} draggable={false} />}
          {nextImage?.url && <img src={nextImage.url} alt="" style={{ ...backgroundImageStyle, position: 'relative' }} draggable={false} />}
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.92), rgba(0,0,0,0.05) 62%)' }} />
        <TopBrand preset={preset} brandingTag={brandingTag} />
        <HeadlineText layout={titleLayout} gradientUrl={resolveAsset(preset.assets.gradient)} preset={preset} titleBottom={1180} />
      </div>
    );
  }

  if (preset.layoutVariant === 'editorial-card') {
    return (
      <div style={{ ...frameStyle, backgroundColor: '#F7F4EF' }}>
        <div style={lightBrandStyle}>{brandingTag.toUpperCase()}</div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            style={{ position: 'absolute', left: 70, top: 120, width: 940, height: 705, objectFit: 'cover' }}
            draggable={false}
          />
        )}
        <EditorialTitle title={title} preset={preset} top={880} color="#101010" accent={preset.colors.accent} />
        {explanation && <EditorialBody explanation={explanation} preset={preset} measure={measure} top={1090} color="#242424" />}
      </div>
    );
  }

  if (preset.layoutVariant === 'poster') {
    return (
      <div style={{ ...frameStyle, background: '#020407' }}>
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            style={{
              position: 'absolute',
              left: 56,
              top: 76,
              width: 968,
              height: 760,
              objectFit: 'cover',
              borderRadius: 34,
              filter: 'saturate(1.16) contrast(1.1) brightness(0.78)',
              boxShadow: `0 0 0 2px rgba(255,255,255,0.10), 0 30px 110px ${preset.colors.accent}35`,
            }}
            draggable={false}
          />
        )}
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 50% 18%, ${preset.colors.accent}22 0%, rgba(0,0,0,0) 38%), linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.98) 33%, rgba(0,0,0,0.62) 62%, rgba(0,0,0,0.08) 100%)` }} />
        <div style={{ position: 'absolute', left: 68, top: 62, right: 68, height: 2, background: `linear-gradient(90deg, transparent, ${preset.colors.accent}, transparent)`, opacity: 0.85 }} />
        <TopBrand preset={preset} brandingTag={brandingTag} />
        <HeadlineText layout={titleLayout} gradientUrl={resolveAsset(preset.assets.gradient)} preset={preset} titleBottom={1092} />
        {explanation && <EditorialBody explanation={explanation} preset={preset} measure={measure} top={1130} color="#F5F7FA" />}
        <div style={{ position: 'absolute', left: 68, bottom: 56, right: 68, display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, ${preset.colors.accent}, transparent)` }} />
          <span style={{ ...headlineFont(preset, 28), color: preset.colors.primary, opacity: 0.86 }}>SWIPE</span>
          <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg, transparent, ${preset.colors.accent})` }} />
        </div>
      </div>
    );
  }

  return (
    <div style={frameStyle}>
      {imageUrl && <img src={imageUrl} alt="" style={backgroundImageStyle} draggable={false} />}
      <BottomGradient fadeTop={220} preset={preset} />
      <TopBrand preset={preset} brandingTag={brandingTag} />
      <HeadlineText layout={titleLayout} gradientUrl={resolveAsset(preset.assets.gradient)} preset={preset} titleBottom={1080} />
      {explanation && <EditorialBody explanation={explanation} preset={preset} measure={measure} top={1125} color="#F5F5F5" />}
      <BottomRule logoUrl={resolveAsset(preset.assets.frontLogo)} preset={preset} />
    </div>
  );
};

const ChatBubble: React.FC<{
  text: string;
  top: number;
  align: 'left' | 'right';
  preset: ReturnType<typeof getCarouselPreset>;
  secondary?: boolean;
}> = ({ text, top, align, preset, secondary }) => (
  <div
    style={{
      position: 'absolute',
      top,
      [align]: 72,
      maxWidth: 690,
      padding: '28px 34px',
      borderRadius: 34,
      background: secondary ? 'rgba(255,255,255,0.78)' : 'rgba(8,8,10,0.82)',
      color: secondary ? '#101010' : preset.colors.primary,
      fontFamily: preset.fonts.body,
      fontSize: secondary ? 34 : 42,
      lineHeight: 1.08,
      boxShadow: '0 20px 60px rgba(0,0,0,0.32)',
    }}
  >
    {text}
  </div>
);

const EditorialTitle: React.FC<{
  title: string;
  preset: ReturnType<typeof getCarouselPreset>;
  top: number;
  color: string;
  accent: string;
}> = ({ title, preset, top, color, accent }) => (
  <div
    style={{
      position: 'absolute',
      top,
      left: 86,
      width: 908,
      color,
      fontFamily: preset.fonts.headline,
      fontSize: 96,
      fontWeight: preset.headline.fontWeight,
      lineHeight: 0.94,
      textTransform: 'uppercase',
    }}
  >
    {title.split(/\s+/).map((word, index) => (
      <span key={`${word}-${index}`} style={{ color: isHighlightedWord(word) ? accent : color }}>
        {word.toUpperCase()}{' '}
      </span>
    ))}
  </div>
);

const EditorialBody: React.FC<{
  explanation: string;
  preset: ReturnType<typeof getCarouselPreset>;
  measure: TextMeasurer;
  top: number;
  color: string;
}> = ({ explanation, preset, measure, top, color }) => {
  const fittedBody = fitBodyText(
    explanation,
    900,
    170,
    preset.explanation.minFontSize,
    preset.explanation.maxFontSize,
    preset.explanation.lineHeightRatio,
    preset.fonts.body,
    preset.explanation.fontWeight,
    measure
  );
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left: 90,
        width: 900,
        color,
        fontFamily: preset.fonts.body,
        fontSize: fittedBody.fontSize,
        lineHeight: `${fittedBody.lineHeight}px`,
        whiteSpace: 'pre',
      }}
    >
      {fittedBody.lines.join('\n')}
    </div>
  );
};

const HeadlineText: React.FC<{
  layout: FitLayout;
  gradientUrl: string;
  preset: ReturnType<typeof getCarouselPreset>;
  titleBottom: number;
}> = ({ layout, gradientUrl, preset, titleBottom }) => {
  let cursor = titleBottom - layout.totalHeight;

  return (
    <>
      {layout.lines.map((line, index) => {
        const lineHeight = line.fontSize * preset.headline.lineHeightRatio;
        const top = cursor;
        cursor += lineHeight;
        return (
          <div
            key={`${line.words.join('-')}-${index}`}
            style={{
              ...headlineFont(preset, line.fontSize),
              position: 'absolute',
              top,
              left: preset.layout.sidePadding,
              width: CANVAS_WIDTH - preset.layout.sidePadding * 2,
              height: lineHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: preset.headline.wordGap,
              lineHeight: `${lineHeight}px`,
              whiteSpace: 'nowrap',
            }}
          >
            {line.words.map((word, wordIndex) => (
              <span key={`${word}-${wordIndex}`} style={wordStyle(word, gradientUrl, preset)}>
                {word.toUpperCase()}
              </span>
            ))}
          </div>
        );
      })}
    </>
  );
};

const TopBrand: React.FC<{ preset: ReturnType<typeof getCarouselPreset>; brandingTag: string }> = ({
  preset,
  brandingTag,
}) => (
  <div
    style={{
      ...headlineFont(preset, 32),
      position: 'absolute',
      top: 20,
      right: 50,
      color: preset.colors.primary,
      opacity: 0.72,
      textShadow: '0 3px 8px rgba(0,0,0,0.65)',
    }}
  >
    {brandingTag.toUpperCase()}
  </div>
);

const BottomRule: React.FC<{ logoUrl: string; preset: ReturnType<typeof getCarouselPreset> }> = ({
  logoUrl,
  preset,
}) => {
  const y = CANVAS_HEIGHT - 20 - preset.layout.logoWidth / 2;
  return (
    <>
      <DecorativeRule preset={preset} y={y} xStart={40} xEnd={480} />
      <img
        src={logoUrl}
        alt=""
        draggable={false}
        style={{
          position: 'absolute',
          left: CANVAS_WIDTH / 2 - preset.layout.logoWidth / 2,
          top: y - preset.layout.logoWidth / 2,
          width: preset.layout.logoWidth,
        }}
      />
    </>
  );
};

const DecorativeRule: React.FC<{
  preset: ReturnType<typeof getCarouselPreset>;
  y: number;
  xStart: number;
  xEnd: number;
}> = ({ preset, y, xStart, xEnd }) => {
  if (!preset.features.showDecorativeLines) return null;

  return (
    <>
      <div style={ruleStyle(xStart, y, xEnd - xStart)} />
      <div style={ruleStyle(CANVAS_WIDTH - xEnd, y, xEnd - xStart)} />
    </>
  );
};

const BottomGradient: React.FC<{ fadeTop: number; preset?: ReturnType<typeof getCarouselPreset> }> = ({ fadeTop, preset }) => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      top: fadeTop,
      height: CANVAS_HEIGHT - fadeTop,
      opacity: preset?.effects.bottomOverlayStrength ?? 1,
      background:
        'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.97) 35%, rgba(0,0,0,0.85) 55%, rgba(0,0,0,0.5) 75%, rgba(0,0,0,0) 100%)',
    }}
  />
);

const wordStyle = (
  word: string,
  gradientUrl: string,
  preset: ReturnType<typeof getCarouselPreset>
): React.CSSProperties => {
  if (isHighlightedWord(word) && preset.features.useGradientMask) {
    const highlightBackground = buildHighlightBackground({
      textureUrl: gradientUrl,
      highlightStart: preset.colors.accent,
      highlightEnd: preset.colors.highlightEnd,
      highlightTextureStrength: preset.effects.highlightTextureStrength,
    });
    return {
      ...highlightBackground,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      color: 'transparent',
      filter: `${highlightBackground.filter || ''} brightness(${preset.effects.highlightIntensity}) saturate(1.12) contrast(1.04)`.trim(),
    };
  }

  return {
    color: isHighlightedWord(word) ? preset.colors.accent : preset.colors.primary,
    textShadow: '0 4px 10px rgba(0,0,0,0.65)',
  };
};

const headlineFont = (
  preset: ReturnType<typeof getCarouselPreset>,
  fontSize: number
): React.CSSProperties => ({
  fontFamily: preset.fonts.headline,
  fontSize,
  fontWeight: preset.headline.fontWeight,
  letterSpacing: 0,
});

const ruleStyle = (left: number, y: number, width: number): React.CSSProperties => ({
  position: 'absolute',
  left,
  top: y,
  width,
  height: 2,
  backgroundColor: 'white',
});

const lightBrandStyle: React.CSSProperties = {
  position: 'absolute',
  top: 72,
  left: 86,
  color: '#101010',
  fontFamily: 'GenericCarouselImpact',
  fontSize: 34,
  letterSpacing: 0,
};

const centerYToTop = (y: number): number => y + CANVAS_HEIGHT / 2;

const resolveAsset = (pathOrUrl: string): string => publicAssetUrl(pathOrUrl);

const frameStyle: React.CSSProperties = {
  position: 'relative',
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT,
  backgroundColor: 'black',
  overflow: 'hidden',
};

const backgroundImageStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};
